import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';
import { formatPrice, calculateWalletBonus, WalletBonusTier } from '../../utils/bookingUtils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bonusTiers: WalletBonusTier[];
}

// Preset top-up amounts
const PRESET_AMOUNTS = [100, 250, 500, 1000, 2000];

// Inner payment form component
const TopUpPaymentForm: React.FC<{
  amount: number;
  bonus: { bonusPercent: number; bonusAmount: number; totalCredit: number };
  onSuccess: () => void;
  onBack: () => void;
}> = ({ amount, bonus, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm payment
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Confirm the top-up on the backend
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CONFIRM_WALLET_TOPUP),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              payment_intent_id: paymentIntent.id,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to confirm top-up');
        }

        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between mb-2">
          <span className="text-gray-700">Top-up Amount:</span>
          <span className="font-medium">${formatPrice(amount)}</span>
        </div>
        {bonus.bonusAmount > 0 && (
          <div className="flex justify-between mb-2 text-green-600">
            <span>Bonus ({bonus.bonusPercent}%):</span>
            <span>+${formatPrice(bonus.bonusAmount)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-blue-200">
          <span className="font-medium text-gray-900">Total Credit:</span>
          <span className="font-bold text-blue-600">${formatPrice(bonus.totalCredit)}</span>
        </div>
      </div>

      {/* Payment Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Details
        </label>
        <PaymentElement />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isProcessing}
          className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || !elements || isProcessing}
          className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Processing...
            </>
          ) : (
            `Pay $${formatPrice(amount)}`
          )}
        </button>
      </div>
    </form>
  );
};

const TopUpModal: React.FC<TopUpModalProps> = ({ isOpen, onClose, onSuccess, bonusTiers }) => {
  const [step, setStep] = useState<'amount' | 'payment' | 'success'>('amount');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topUpResult, setTopUpResult] = useState<{
    topupAmount: number;
    bonusAmount: number;
    totalCredit: number;
    newBalance: number;
  } | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('amount');
      setSelectedAmount(null);
      setCustomAmount('');
      setClientSecret(null);
      setError(null);
      setTopUpResult(null);
    }
  }, [isOpen]);

  // Get the final amount (selected or custom)
  const getFinalAmount = (): number => {
    if (selectedAmount) return selectedAmount;
    const parsed = parseFloat(customAmount);
    return isNaN(parsed) ? 0 : parsed;
  };

  const amount = getFinalAmount();
  const bonus = calculateWalletBonus(amount, bonusTiers);

  const handleContinueToPayment = async () => {
    if (amount < 10) {
      setError('Minimum top-up amount is $10');
      return;
    }

    setIsCreatingIntent(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Check if user is authenticated
      if (!session?.access_token) {
        throw new Error('Please log in to add funds to your wallet');
      }

      const response = await fetch(
        edgeFunctionUrl(EdgeFunctions.CREATE_WALLET_TOPUP),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ amount }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create top-up');
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
      setStep('payment');
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = () => {
    setTopUpResult({
      topupAmount: amount,
      bonusAmount: bonus.bonusAmount,
      totalCredit: bonus.totalCredit,
      newBalance: 0, // Will be updated when modal closes
    });
    setStep('success');
  };

  const handleClose = () => {
    if (step === 'success') {
      onSuccess();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'success' ? 'Top-Up Successful!' : 'Add Funds to Wallet'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Step 1: Select Amount */}
          {step === 'amount' && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Select an amount to add to your wallet. Get bonus credits on larger top-ups!
              </p>

              {/* Preset Amounts */}
              <div className="grid grid-cols-3 gap-2">
                {PRESET_AMOUNTS.map((preset) => {
                  const presetBonus = calculateWalletBonus(preset, bonusTiers);
                  return (
                    <button
                      key={preset}
                      onClick={() => {
                        setSelectedAmount(preset);
                        setCustomAmount('');
                      }}
                      className={`p-3 rounded-lg border-2 text-center transition-colors ${
                        selectedAmount === preset
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-gray-900">${preset}</span>
                      {presetBonus.bonusPercent > 0 && (
                        <span className="block text-xs text-green-600">
                          +{presetBonus.bonusPercent}% bonus
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Custom Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Or enter custom amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    min="10"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    placeholder="Enter amount"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Preview */}
              {amount >= 10 && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">You pay:</span>
                    <span className="font-medium">${formatPrice(amount)}</span>
                  </div>
                  {bonus.bonusAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Bonus ({bonus.bonusPercent}%):</span>
                      <span>+${formatPrice(bonus.bonusAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-200 mt-2">
                    <span>You receive:</span>
                    <span className="text-blue-600">${formatPrice(bonus.totalCredit)}</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleContinueToPayment}
                disabled={amount < 10 || isCreatingIntent}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isCreatingIntent ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Processing...
                  </>
                ) : (
                  'Continue to Payment'
                )}
              </button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 'payment' && clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#2563eb',
                  },
                },
              }}
            >
              <TopUpPaymentForm
                amount={amount}
                bonus={bonus}
                onSuccess={handlePaymentSuccess}
                onBack={() => {
                  setStep('amount');
                  setClientSecret(null);
                }}
              />
            </Elements>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Funds Added Successfully!
              </h3>
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <p className="text-green-800">
                  <span className="font-medium">${formatPrice(bonus.totalCredit)}</span> has been added to your wallet
                </p>
                {bonus.bonusAmount > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    Includes ${formatPrice(bonus.bonusAmount)} bonus!
                  </p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;
